import { EyeIcon, FolderIcon, LayoutGridIcon } from "lucide-react";
import * as React from "react";
import { useGetFavoritesQuery, useRemoveFavoriteMutation } from "@/api/files";
import {
  useGetSnapshotCollectionFavoritesQuery,
  useDeleteSnapshotCollectionFavoriteMutation,
} from "@/modules/snapshots/snapshotCollection.api";
import { buildSnapshotGridPath } from "@/modules/snapshots/routes";
import { SidebarSeparator } from "@/components/sidebar/SidebarSeparator";
import { FavoriteItem } from "./FavoriteItem";
import { useGetSnapshotViewFavoritesQuery } from "@/modules/snapshots/snapshotViews.api";
import { buildSnapshotViewHierarchy } from "@/modules/snapshots/snapshotViews/snapshotViewHierarchy";
import { SnapshotViewPinButton } from "@/modules/snapshots/snapshotViews/SnapshotViewPinButton";
import { Link } from "react-router";

export const SidebarFavorites: React.FC = () => {
  const { data: favorites = [] } = useGetFavoritesQuery();
  const [removeFavorite, { isLoading: isRemoving }] = useRemoveFavoriteMutation();

  const { data: snapshotCollectionFavorites = [] } = useGetSnapshotCollectionFavoritesQuery();
  const [removeSnapshotCollectionFavorite, { isLoading: isRemovingCollection }] =
    useDeleteSnapshotCollectionFavoriteMutation();
  const { data: snapshotViews = [] } = useGetSnapshotViewFavoritesQuery();
  const snapshotViewGroups = React.useMemo(() => buildSnapshotViewHierarchy(snapshotViews), [snapshotViews]);

  const handleRemoveFavorite = async (fsEntryId: string) => {
    try {
      await removeFavorite(fsEntryId).unwrap();
    } catch (error) {
      console.error("Failed to remove favorite:", error);
    }
  };

  const handleRemoveSnapshotCollectionFavorite = async (snapshotCollectionId: string) => {
    try {
      await removeSnapshotCollectionFavorite({ snapshot_collection_id: snapshotCollectionId }).unwrap();
    } catch (error) {
      console.error("Failed to remove snapshot collection favorite:", error);
    }
  };

  if (favorites.length === 0 && snapshotCollectionFavorites.length === 0 && snapshotViews.length === 0) {
    return null;
  }

  return (
    <>
      <SidebarSeparator />
      <div className="space-y-1">
        <div className="space-y-0.5 text-ui-sidebar-text">
          {favorites.length > 0 && (
            <div className="pt-3">
              <p className="px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Folders</p>
              {favorites.map(favorite => (
                <FavoriteItem
                  key={favorite.id}
                  fsEntryId={favorite.fs_entry_id}
                  folderName={favorite.folder_name}
                  onRemove={handleRemoveFavorite}
                  isRemoving={isRemoving}
                  type="folder"
                />
              ))}
            </div>
          )}
          {snapshotCollectionFavorites.length > 0 && (
            <div className="pt-3">
              <p className="px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Collections</p>
              {snapshotCollectionFavorites.map(favorite => (
                <FavoriteItem
                  key={`snapshot-${favorite.id}`}
                  fsEntryId={favorite.snapshot_collection_id}
                  folderName={favorite.name}
                  onRemove={handleRemoveSnapshotCollectionFavorite}
                  isRemoving={isRemovingCollection}
                  type="snapshot"
                  linkPath={buildSnapshotGridPath(favorite.business_report_id, favorite.snapshot_collection_id)}
                />
              ))}
            </div>
          )}
          {snapshotViewGroups.length > 0 && (
            <div className="pt-3">
              <p className="px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Views</p>
              {snapshotViewGroups.map(template => (
                <div key={template.id} className="mt-1">
                  <div className="flex items-center gap-1.5 px-2 py-1 text-xs font-semibold text-gray-700">
                    <LayoutGridIcon className="h-4 w-4 shrink-0 text-coral" aria-hidden="true" />
                    <span className="truncate" title={template.name}>
                      {template.name}
                    </span>
                  </div>
                  {template.collections.map(collection => (
                    <div key={`${template.id}:${collection.id}`} className="pl-3">
                      <div className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-gray-600">
                        <FolderIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                        <span className="truncate" title={collection.name}>
                          {collection.name}
                        </span>
                      </div>
                      {collection.views.map(view => (
                        <div key={view.id} className="group flex items-center pl-3 pr-1">
                          <Link
                            to={`/snapshots/views/${view.id}`}
                            className="text-atriablue-500 flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-white hover:text-gray-600"
                          >
                            <EyeIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                            <span className="truncate" title={view.name}>
                              {view.name}
                            </span>
                          </Link>
                          <SnapshotViewPinButton view={view} compact />
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
